import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';

import { BlackSparkleIcon } from 'src/assets/black-sparkle-icon';
import { buildConciergeOverridesCss, conciergeThemeAttribute } from 'src/concierge/concierge-theme';
import { ConciergeVariables } from 'src/concierge/concierge-variables';
import { useConciergeTheme } from 'src/concierge/use-concierge-theme';
import {
  baseTravel,
  SVG_PAD,
  useButtonMetrics,
} from 'src/dispensary/core-menu/components/header/concierge-nav-button/concierge-button-effects';
import type { ProductListConciergeEntry } from './product-list-concierge-content';

const RING_LENGTH = 34;

/**
 * Themed from the store's Concierge theme (partner, Admin, then stock), scoped
 * to this card: `concierge-mode` and `data-concierge-theme` re-skin only the
 * card's own `--dcm-*` tokens.
 */
export function ProductListConciergeCard({ content, onClick }: ProductListConciergeEntry): JSX.Element {
  const { settings } = useConciergeTheme();
  const themeAttribute = conciergeThemeAttribute(settings);
  const overridesCss = useMemo(() => buildConciergeOverridesCss(settings), [settings]);

  const shellRef = useRef<HTMLElement>(null);
  const metrics = useButtonMetrics(shellRef);

  const rectProps = metrics
    ? {
        x: SVG_PAD,
        y: SVG_PAD,
        width: metrics.w,
        height: metrics.h,
        rx: Math.min(metrics.radius, metrics.h / 2),
        pathLength: 100,
      }
    : null;

  return (
    <>
      <ConciergeVariables $tenantOverrides={overridesCss} />
      <Card
        ref={shellRef}
        aria-label='AI Concierge'
        className='concierge-mode'
        data-concierge-theme={themeAttribute ?? undefined}
        data-testid='product-list-concierge-card'
      >
        {metrics && rectProps && (
          <RingSvg
            aria-hidden='true'
            data-testid='product-list-concierge-card-ring'
            viewBox={`0 0 ${metrics.w + SVG_PAD * 2} ${metrics.h + SVG_PAD * 2}`}
          >
            <BaseRing {...rectProps} />
            <TravelRing {...rectProps} data-testid='product-list-concierge-card-ring-travel' />
          </RingSvg>
        )}
        <GlowClip aria-hidden='true' />
        <Content>
          <Sparkle aria-hidden='true'>
            <BlackSparkleIcon width={24} height={24} fill='currentColor' />
          </Sparkle>
          <Label>{content.sectionLabel}</Label>
          <Headline title={content.headline}>{content.headline}</Headline>
          <Subtext>{content.subtext}</Subtext>
          <Cta type='button' onClick={onClick}>
            {content.button}
          </Cta>
        </Content>
      </Card>
    </>
  );
}

const Card = styled.section`
  background: radial-gradient(
    120% 140% at 15% 0%,
    color-mix(in oklch, var(--dcm-color-surface-hover, #182f28) 75%, var(--dcm-concierge-accent, #d4a843)) 0%,
    var(--dcm-color-surface, #0e201a) 55%,
    color-mix(in oklch, var(--dcm-color-surface, #0e201a) 70%, black) 100%
  );
  border-radius: var(--dcm-radius-cards, 8px);
  color: var(--dcm-color-text-primary, #f8efd9);
  grid-column: 1 / -1;
  isolation: isolate;
  padding: 22px 18px;
  position: relative;
  width: 100%;
`;

const RingSvg = styled.svg`
  height: calc(100% + ${SVG_PAD * 2}px);
  left: -${SVG_PAD}px;
  overflow: visible;
  pointer-events: none;
  position: absolute;
  top: -${SVG_PAD}px;
  width: calc(100% + ${SVG_PAD * 2}px);
`;

const BaseRing = styled.rect`
  fill: none;
  stroke: var(--dcm-concierge-accent, #d4a843);
  stroke-opacity: 0.25;
  stroke-width: 1;
`;

const TravelRing = styled.rect`
  fill: none;
  filter: drop-shadow(0 0 4px var(--dcm-concierge-accent, #d4a843));
  stroke: var(--dcm-concierge-accent, #d4a843);
  stroke-dasharray: ${RING_LENGTH} ${100 - RING_LENGTH};
  stroke-linecap: round;
  stroke-width: 2.5;

  @media (prefers-reduced-motion: no-preference) {
    animation: ${baseTravel} 3s linear infinite;
  }
`;

/* Clips the sparkle glow to the card without clipping the ring's own glow. */
const GlowClip = styled.div`
  border-radius: inherit;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  position: absolute;

  /* Centered on the sparkle: card top padding (22px) + half the icon (20px). */
  &::before {
    background: radial-gradient(
      closest-side,
      color-mix(in oklch, var(--dcm-concierge-accent, #d4a843) 22%, transparent),
      transparent 70%
    );
    content: '';
    height: 220px;
    left: 50%;
    position: absolute;
    top: 42px;
    transform: translate(-50%, -50%);
    width: 220px;
  }
`;

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0 auto;
  max-width: 640px;
  min-width: 0;
  position: relative;
  text-align: center;
`;

const Sparkle = styled.span`
  align-items: center;
  background: linear-gradient(
    150deg,
    var(--dcm-concierge-accent-soft, rgba(212, 168, 67, 0.3)),
    var(--dcm-concierge-accent-glow, rgba(212, 168, 67, 0.15))
  );
  border-radius: var(--dcm-product-list-concierge-card-icon-border-radius, 50%);
  color: var(--dcm-concierge-accent, #d4a843);
  display: flex;
  height: 40px;
  justify-content: center;
  position: relative;
  width: 40px;
`;

const Label = styled.p`
  color: var(--dcm-concierge-accent, #d4a843);
  font-size: var(--dcm-font-size-xs, 12px);
  font-weight: var(--dcm-font-weight-bold, 700);
  letter-spacing: 0.08em;
  margin: 0;
`;

const Headline = styled.h2`
  font-family: var(--dcm-font-primary, inherit);
  font-size: var(--dcm-font-size-lg, 16px);
  font-weight: var(--dcm-font-weight-bold, 700);
  line-height: 1.25;
  margin: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: var(--dcm-font-size-xl, 18px);
  }
`;

const Subtext = styled.p`
  color: var(--dcm-color-text-secondary, #889591);
  font-size: var(--dcm-font-size-sm, 13px);
  font-weight: var(--dcm-font-weight-normal, 400);
  margin: 0;
`;

const Cta = styled.button`
  background: linear-gradient(135deg, var(--dcm-concierge-accent, #d4a843), var(--dcm-concierge-accent-deep, #b88e35));
  border: none;
  border-radius: var(--dcm-radius-buttons, 4px);
  color: var(--dcm-color-text-inverted, #0e201a);
  cursor: pointer;
  font-size: var(--dcm-font-size-sm, 13px);
  font-weight: var(--dcm-font-weight-bold, 700);
  margin-top: 4px;
  padding: 9px 18px;
  transition: filter 0.2s;

  @media (hover: hover) {
    &:hover {
      filter: brightness(1.06);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--dcm-concierge-accent, #d4a843);
    outline-offset: 3px;
  }
`;
